import axios from 'axios';
import {
  WORKFLOW_STAGE,
  getApproval,
  isPendingApproval,
} from '@/lib/workflowHelpers';

/** Slightly above incidentsapi WORKFLOW_PROXY_TIMEOUT (default 180s). */
export const WORKFLOW_DECISION_TIMEOUT_MS = 190_000;

export function isWorkflowDecisionTransportError(error) {
  const status = error?.response?.status;
  if (status === 502 || status === 503 || status === 504) return true;
  const code = String(error?.code || '');
  if (code === 'ECONNABORTED' || code === 'ERR_NETWORK') return true;
  const msg = String(error?.message || '').toLowerCase();
  return msg.includes('network error') || msg.includes('timeout');
}

/**
 * After a gateway/orchestrator timeout, the NUP path often already persisted
 * approval and moved the ticket to PROVISIONING while MCP work continues.
 */
export function inferWorkflowDecisionProgress(ticketBefore, ticketAfter, { approved }) {
  if (!ticketAfter) return { kind: 'unknown' };

  const beforePending = isPendingApproval(ticketBefore);
  const approval = getApproval(ticketAfter);
  const status = String(approval?.status || '').toLowerCase();
  const stage = String(ticketAfter?.workflow_stage || '');

  if (approved) {
    const decisionRecorded =
      status === 'approved' || (beforePending && !isPendingApproval(ticketAfter));
    if (decisionRecorded) {
      if (stage === WORKFLOW_STAGE.FAILED) return { kind: 'provision_failed' };
      if (stage === WORKFLOW_STAGE.ESCALATED) return { kind: 'escalated' };
      if (stage === WORKFLOW_STAGE.DONE) return { kind: 'completed' };
      if (
        stage === WORKFLOW_STAGE.PROVISIONING
        || stage === WORKFLOW_STAGE.TERMINATING
        || stage === WORKFLOW_STAGE.SCHEDULED
      ) {
        return { kind: 'in_progress' };
      }
      if (!isPendingApproval(ticketAfter)) return { kind: 'completed' };
    }
  } else if (status === 'rejected' || stage === WORKFLOW_STAGE.REJECTED) {
    return { kind: 'rejected' };
  }

  if (beforePending && isPendingApproval(ticketAfter)) {
    return { kind: 'still_pending' };
  }
  return { kind: 'unknown' };
}

export function postWorkflowDecision(url, payload, { timeout = WORKFLOW_DECISION_TIMEOUT_MS } = {}) {
  return axios.post(url, payload, { timeout });
}

export async function refreshTicketAfterDecisionTransportError(
  fetchTicket,
  { attempts = 4, delayMs = 1500 } = {},
) {
  let latest = null;
  for (let i = 0; i < attempts; i += 1) {
    // eslint-disable-next-line no-await-in-loop
    latest = await fetchTicket();
    if (latest && !isPendingApproval(latest)) break;
    if (i < attempts - 1) {
      // eslint-disable-next-line no-await-in-loop
      await new Promise((resolve) => {
        setTimeout(resolve, delayMs);
      });
    }
  }
  return latest;
}
